import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    TextField,
    Button,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress,
    Alert,
    Collapse,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
} from '@mui/material';
import {
    Edit,
    Save,
    Cancel,
    ExpandMore,
    ExpandLess,
    EditNote,
    AutoAwesome,
} from '@mui/icons-material';
import { useStores } from '../../hooks/use-stores';
import LocaleService from '../../services/LocaleService';
import { questionDefinitionToTranslationKeys } from '../../utils/questionDefinitionToTranslationKeys';

const NAMESPACE = 'assessment';

/**
 * Subset of the language editor: edits only translation keys that appear in the question definition's UI section.
 * Uses assessment namespace and LocaleService (same as LanguagesEditor), not company/partner overrides.
 */
const QuestionTranslationsEditor = ({ definition = '' }) => {
    const { superStore } = useStores();
    const [language, setLanguage] = useState('en');
    const [localeData, setLocaleData] = useState({});
    const [editingKey, setEditingKey] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [changes, setChanges] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [existingExpanded, setExistingExpanded] = useState(true);
    const [missingExpanded, setMissingExpanded] = useState(true);
    const [commitDialogOpen, setCommitDialogOpen] = useState(false);
    const [commitMessage, setCommitMessage] = useState('');
    const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
    const [bulkEditKey, setBulkEditKey] = useState('');
    const [bulkEditValues, setBulkEditValues] = useState({});
    const [allLanguagesData, setAllLanguagesData] = useState({});
    const [loadingBulkData, setLoadingBulkData] = useState(false);
    const [languageCache, setLanguageCache] = useState({});
    const [loadingBulkKey, setLoadingBulkKey] = useState(null);
    const [translatingAI, setTranslatingAI] = useState(false);

    const languages = superStore.languages || [];

    const questionKeys = useMemo(
        () => questionDefinitionToTranslationKeys(definition),
        [definition]
    );

    useEffect(() => {
        if (!superStore.languages || superStore.languages.length === 0) {
            superStore.loadLanguages();
        }
    }, []);

    useEffect(() => {
        if (languages.length > 0 && !languages.find((l) => l.code === language)) {
            const enLang = languages.find((l) => l.code === 'en');
            setLanguage(enLang ? 'en' : languages[0]?.code || 'en');
        }
    }, [languages, language]);

    useEffect(() => {
        if (questionKeys.length === 0) return;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                let data = {};
                try {
                    data = await LocaleService.fetchLocale(language, NAMESPACE);
                } catch (e) {
                    if (e.response && (e.response.status === 403 || e.response.status === 404)) {
                        data = {};
                    } else throw e;
                }
                setLocaleData(data);
            } catch (e) {
                setError(`Failed to load locale: ${e.message}`);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [language, questionKeys.length]);

    const changeKey = `${NAMESPACE}:${language}`;
    const displayData = useMemo(() => {
        const base = { ...localeData };
        if (changes[changeKey]) {
            Object.keys(changes[changeKey]).forEach((key) => {
                base[key] = changes[changeKey][key];
            });
        }
        return base;
    }, [localeData, changes, changeKey]);

    const existingKeys = useMemo(
        () => questionKeys.filter((key) => displayData[key] !== undefined && displayData[key] !== ''),
        [questionKeys, displayData]
    );
    const missingKeys = useMemo(
        () => questionKeys.filter((key) => !displayData[key] || displayData[key] === ''),
        [questionKeys, displayData]
    );
    const hasChanges = Object.keys(changes).length > 0;

    const handleEdit = (key, value) => {
        setEditingKey(key);
        setEditValue(value || '');
    };

    const handleSave = (key) => {
        const updatedChanges = { ...changes };
        if (!updatedChanges[changeKey]) updatedChanges[changeKey] = {};
        updatedChanges[changeKey][key] = editValue;
        setChanges(updatedChanges);
        setLocaleData((prev) => ({ ...prev, [key]: editValue }));
        setEditingKey(null);
        setEditValue('');
    };

    const handleCancel = () => {
        setEditingKey(null);
        setEditValue('');
    };

    const handleSaveAll = () => {
        setCommitDialogOpen(true);
    };

    const handleCommitSave = async () => {
        if (!commitMessage.trim()) {
            setError('Commit message is required');
            return;
        }
        setCommitDialogOpen(false);
        setLoading(true);
        setError(null);
        setSuccess(false);
        try {
            const transformedChanges = {};
            Object.keys(changes).forEach((key) => {
                const [ns, lang] = key.split(':');
                if (!transformedChanges[ns]) transformedChanges[ns] = {};
                if (!transformedChanges[ns][lang]) transformedChanges[ns][lang] = {};
                transformedChanges[ns][lang] = changes[key];
            });
            await LocaleService.saveLocaleChanges({
                message: commitMessage,
                data: transformedChanges,
            });
            setSuccess(true);
            setChanges({});
            setCommitMessage('');
            const data = await LocaleService.fetchLocale(language, NAMESPACE).catch(() => ({}));
            setLocaleData(data);
        } catch (e) {
            setError(`Failed to save: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleCommitCancel = () => {
        setCommitDialogOpen(false);
        setCommitMessage('');
    };

    const handleBulkEdit = async (key) => {
        setBulkEditKey(key);
        setLoadingBulkKey(key);
        try {
            const hasUnsavedChanges = Object.keys(changes).some(
                (ck) => changes[ck][key] !== undefined
            );
            const cacheKey = `${NAMESPACE}:${key}`;
            if (!hasUnsavedChanges && languageCache[cacheKey]) {
                setAllLanguagesData(languageCache[cacheKey]);
                setBulkEditValues(languageCache[cacheKey]);
                setBulkEditDialogOpen(true);
                setLoadingBulkKey(null);
                return;
            }
            setLoadingBulkData(true);
            const allData = {};
            await Promise.all(
                languages.map(async (lang) => {
                    try {
                        const data = await LocaleService.fetchLocale(lang.code, NAMESPACE);
                        allData[lang.code] = data[key] || '';
                    } catch {
                        allData[lang.code] = '';
                    }
                })
            );
            languages.forEach((lang) => {
                const ck = `${NAMESPACE}:${lang.code}`;
                if (changes[ck] && changes[ck][key] !== undefined) {
                    allData[lang.code] = changes[ck][key];
                }
            });
            if (!hasUnsavedChanges) {
                setLanguageCache((prev) => ({ ...prev, [cacheKey]: allData }));
            }
            setAllLanguagesData(allData);
            setBulkEditValues(allData);
            setBulkEditDialogOpen(true);
        } catch (e) {
            setError('Failed to load data for bulk edit');
        } finally {
            setLoadingBulkData(false);
            setLoadingBulkKey(null);
        }
    };

    const handleBulkEditApply = () => {
        const updatedChanges = { ...changes };
        Object.keys(bulkEditValues).forEach((langCode) => {
            const newValue = bulkEditValues[langCode];
            const originalValue = allLanguagesData[langCode] || '';
            if (newValue !== originalValue) {
                const ck = `${NAMESPACE}:${langCode}`;
                if (!updatedChanges[ck]) updatedChanges[ck] = {};
                updatedChanges[ck][bulkEditKey] = newValue;
            }
        });
        setChanges(updatedChanges);
        if (bulkEditValues[language] !== undefined) {
            setLocaleData((prev) => ({ ...prev, [bulkEditKey]: bulkEditValues[language] }));
        }
        setBulkEditDialogOpen(false);
        setBulkEditKey('');
        setBulkEditValues({});
        setAllLanguagesData({});
    };

    const handleBulkEditCancel = () => {
        setBulkEditDialogOpen(false);
        setBulkEditKey('');
        setBulkEditValues({});
        setAllLanguagesData({});
    };

    const handleFillWithAI = async () => {
        setTranslatingAI(true);
        setError(null);
        try {
            const emptyLanguages = languages
                .map((l) => l.code)
                .filter((code) => !bulkEditValues[code] || bulkEditValues[code].trim() === '');
            if (emptyLanguages.length === 0) {
                setError('No empty fields to translate');
                setTranslatingAI(false);
                return;
            }
            let currentValue = null;
            if (bulkEditValues['en'] && bulkEditValues['en'].trim()) {
                currentValue = { lang: 'en', value: bulkEditValues['en'] };
            } else {
                for (const lang of languages) {
                    if (bulkEditValues[lang.code] && bulkEditValues[lang.code].trim()) {
                        currentValue = { lang: lang.code, value: bulkEditValues[lang.code] };
                        break;
                    }
                }
            }
            if (!currentValue) {
                setError('No source text for translation');
                setTranslatingAI(false);
                return;
            }
            const translations = await LocaleService.translateText(emptyLanguages, currentValue);
            setBulkEditValues((prev) => ({ ...prev, ...translations }));
        } catch (e) {
            setError(`Translate failed: ${e.message || 'Unknown error'}`);
        } finally {
            setTranslatingAI(false);
        }
    };

    const areAllFieldsEmpty = () =>
        languages.every((l) => !bulkEditValues[l.code] || bulkEditValues[l.code].trim() === '');

    const renderKeyTable = (keysToShow, emptyMessage) => (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: '30%', fontWeight: 'bold' }}>Key</TableCell>
                        <TableCell sx={{ width: '60%', fontWeight: 'bold' }}>Value</TableCell>
                        <TableCell sx={{ width: '10%', fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {keysToShow.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={3}>
                                <Typography color="text.secondary">{emptyMessage}</Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        keysToShow.map((key) => (
                            <TableRow key={key}>
                                <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                                    {key}
                                </TableCell>
                                <TableCell>
                                    {editingKey === key ? (
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            autoFocus
                                        />
                                    ) : (
                                        <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                            {displayData[key] ?? ''}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {editingKey === key ? (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton size="small" color="primary" onClick={() => handleSave(key)}>
                                                <Save />
                                            </IconButton>
                                            <IconButton size="small" color="secondary" onClick={handleCancel}>
                                                <Cancel />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEdit(key, displayData[key])}
                                            >
                                                <Edit />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleBulkEdit(key)}
                                                title="Bulk edit across languages"
                                                disabled={loadingBulkKey === key}
                                            >
                                                {loadingBulkKey === key ? (
                                                    <CircularProgress size={16} />
                                                ) : (
                                                    <EditNote />
                                                )}
                                            </IconButton>
                                        </Box>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    if (questionKeys.length === 0) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography color="text.secondary">
                    Save a valid question definition above with a <code>ui</code> section to see
                    translation keys (title, blocks[].title, options[].text, answer.heading[].text,
                    etc.).
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
                        Question translation keys (assessment)
                    </Typography>
                    <Paper sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel>Language</InputLabel>
                                <Select
                                    value={language}
                                    label="Language"
                                    onChange={(e) => setLanguage(e.target.value)}
                                >
                                    {languages.map((lang) => (
                                        <MenuItem key={lang.code} value={lang.code}>
                                            {lang.name} ({lang.code})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Paper>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
                            Changes saved successfully!
                        </Alert>
                    )}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Paper sx={{ mb: 3 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setExistingExpanded(!existingExpanded)}
                                >
                                    <Typography variant="h6">
                                        With value ({existingKeys.length})
                                    </Typography>
                                    <IconButton size="small">
                                        {existingExpanded ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>
                                </Box>
                                <Collapse in={existingExpanded}>
                                    {renderKeyTable(
                                        existingKeys,
                                        'No keys with values in this language.'
                                    )}
                                </Collapse>
                            </Paper>

                            {missingKeys.length > 0 && (
                                <Paper sx={{ mb: 3 }}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => setMissingExpanded(!missingExpanded)}
                                    >
                                        <Typography variant="h6">
                                            Empty / missing ({missingKeys.length})
                                        </Typography>
                                        <IconButton size="small">
                                            {missingExpanded ? <ExpandLess /> : <ExpandMore />}
                                        </IconButton>
                                    </Box>
                                    <Collapse in={missingExpanded}>
                                        {renderKeyTable(
                                            missingKeys,
                                            'All keys have values.'
                                        )}
                                    </Collapse>
                                </Paper>
                            )}
                        </>
                    )}
                </Box>

                <Box
                    sx={{
                        width: 400,
                        flexShrink: 0,
                        borderLeft: 1,
                        borderColor: 'divider',
                        p: 2,
                        overflow: 'auto',
                        bgcolor: 'background.paper',
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Pending changes
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {!hasChanges ? (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                            No pending changes
                        </Typography>
                    ) : (
                        <>
                            <List sx={{ overflow: 'auto', flex: 1 }}>
                                {Object.keys(changes).map((ck) => (
                                    <Box key={ck} sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            {ck}
                                        </Typography>
                                        {Object.keys(changes[ck]).map((key) => (
                                            <ListItem
                                                key={key}
                                                sx={{
                                                    display: 'block',
                                                    bgcolor: 'grey.50',
                                                    mb: 1,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ fontWeight: 'bold', wordBreak: 'break-word', pr: 4 }}
                                                        >
                                                            {key}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                whiteSpace: 'pre-wrap',
                                                                wordBreak: 'break-word',
                                                                fontSize:
                                                                    changes[ck][key] === ''
                                                                        ? '1.1em'
                                                                        : 'inherit',
                                                                fontStyle:
                                                                    changes[ck][key] === ''
                                                                        ? 'italic'
                                                                        : 'normal',
                                                                color:
                                                                    changes[ck][key] === ''
                                                                        ? 'text.secondary'
                                                                        : 'inherit',
                                                            }}
                                                        >
                                                            {changes[ck][key] === '' ? '<EMPTY>' : changes[ck][key]}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </Box>
                                ))}
                            </List>
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    startIcon={<Save />}
                                    onClick={handleSaveAll}
                                    disabled={loading}
                                >
                                    Save all changes
                                </Button>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    {Object.keys(changes).reduce(
                                        (acc, k) => acc + Object.keys(changes[k]).length,
                                        0
                                    )}{' '}
                                    change(s)
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>

            <Dialog open={commitDialogOpen} onClose={handleCommitCancel} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm save</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Save these translation changes to the assessment locale? A commit message is
                        required.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Commit message"
                        value={commitMessage}
                        onChange={(e) => setCommitMessage(e.target.value)}
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCommitCancel}>Cancel</Button>
                    <Button
                        onClick={handleCommitSave}
                        variant="contained"
                        color="primary"
                        startIcon={<Save />}
                        disabled={!commitMessage.trim()}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={bulkEditDialogOpen}
                onClose={handleBulkEditCancel}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Bulk edit: {bulkEditKey}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Edit this key across all languages:
                    </Typography>
                    {loadingBulkData ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {languages.map((lang) => (
                                <Grid item xs={12} sm={6} key={lang.code}>
                                    <TextField
                                        fullWidth
                                        label={`${lang.name} (${lang.code})`}
                                        multiline
                                        rows={2}
                                        value={bulkEditValues[lang.code] || ''}
                                        onChange={(e) =>
                                            setBulkEditValues((prev) => ({
                                                ...prev,
                                                [lang.code]: e.target.value,
                                            }))
                                        }
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleBulkEditCancel} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleFillWithAI}
                        variant="outlined"
                        color="primary"
                        startIcon={
                            translatingAI ? <CircularProgress size={16} /> : <AutoAwesome />
                        }
                        disabled={
                            loadingBulkData || translatingAI || areAllFieldsEmpty()
                        }
                        sx={{ mr: 'auto' }}
                    >
                        Fill with AI
                    </Button>
                    <Button
                        onClick={handleBulkEditApply}
                        variant="contained"
                        color="primary"
                        startIcon={<Save />}
                        disabled={loadingBulkData || translatingAI}
                    >
                        Apply changes
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default QuestionTranslationsEditor;
